import React, { useState, useEffect } from 'react';
import { Scissors, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { createClient } from 'contentful';
import { useStore } from '../store';

// Create Contentful client
const contentfulClient = createClient({
  space: '1mf3gv96yevn',
  accessToken: 'j5cAtOPiX6gEZLVMHLSNVdSIcqx1vq5CaEbbkKGC52Y',
});

export default function SalonGallery() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  
  // Get animation state from store
  const isModelAnimationComplete = useStore((state) => state.isModelAnimationComplete);
  const order = 'asc';

  useEffect(() => {
    // Fetch images from Contentful
    // HOW TO ORDER THESE:
    // BY TITLE:       'fields.title'
    // BY UPLOAD ASC:  'sys.createdAt'
    // BY UPLOAD DESC: '-sys.createdAt
    // BY UPLOAD THEN BY TITLE: ['fields.category', 'fields.title']
    const fetchImages = async () => {
      try {
        const response = await contentfulClient.getEntries({
          content_type: 'galleryImage', // Change to your content type ID
          order:  order === 'asc' ? 'sys.createdAt' : '-sys.createdAt', // If order = "asc", old images first
          include: 2, // Include linked assets (images)
        });

        if (response.items.length > 0) {
          // Process the images
          const processedImages = response.items.map(item => {
            const imageUrl = item.fields.image?.fields?.file?.url;
            const title = item.fields.title || 'Salon Image';
            const description = item.fields.description || '';
            const category = item.fields.category || 'Uncategorized';
            
            // Only return images with valid URLs
            if (!imageUrl) return null;
            
            return {
              url: `https:${imageUrl}`,
              title,
              description,
              category,
              id: item.sys.id,
            };
          }).filter(Boolean); // Remove null items
          
          setImages(processedImages);
          
          // Extract unique categories
          const uniqueCategories = ['all', ...new Set(processedImages.map(img => img.category))];
          setCategories(uniqueCategories);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching images from Contentful:', error);
        setError('Failed to load gallery images. Please try again later.');
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Filter images by selected category
  const filteredImages = selectedCategory === 'all' 
    ? images 
    : images.filter(img => img.category === selectedCategory);
    
  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev + 1) % filteredImages.length);
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  const handleImageError = (e) => {
    console.error(`Failed to load image: ${e.target.src}`);
    e.target.src = '/images/fallback.png';
  };

  const setupCloseButton = (buttonElement) => {
    if (!buttonElement) return;
    
    // Add these attributes to ensure iOS Safari recognizes it as a clickable element
    buttonElement.setAttribute('role', 'button');
    
    // Add the actual close functionality with multiple event types for cross-browser support
    const closeGallery = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setIsDialogOpen(false);
    };
    
    // Add a no-op click listener (this helps iOS Safari recognize it as interactive)
    buttonElement.addEventListener('click', () => {}, { passive: false });
    
    buttonElement.addEventListener('click', closeGallery, { passive: false });
    buttonElement.addEventListener('touchend', closeGallery, { passive: false });
  };
  
  
  return (
    <div>
      {/* Button to open gallery modal */}
      {isModelAnimationComplete && (
        <div className="fixed z-50 lg:bottom-24 bottom-24 left-1/2 transform -translate-x-1/2 opacity-0 animate-fade-in">
          <button
            onClick={() => setIsDialogOpen(true)}
            className="relative group bg-pink-200 text-pink-400 px-8 py-2.5 rounded-lg 
              hover:bg-pink-400 hover:text-pink-200 transition-all duration-300 min-w-[250px]
              flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
          >
            <Scissors className="w-5 h-5" />
            Browse Salon Gallery
          </button>
        </div>
      )}

      {/* Gallery Modal */}
      {isDialogOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] 
            flex items-center justify-center p-4"
          onClick={() => setIsDialogOpen(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-5xl h-[85vh] flex flex-col
              shadow-2xl relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Close Button */}
            <button
                ref={setupCloseButton}
                className="absolute top-1 right-1 z-[20] text-gray-500 hover:text-gray-800 rounded-full p-6 ios-touch-fix" // Added ios-touch-fix class
                aria-label="Close gallery"
                >
                    <X className="w-6 h-6" />
            </button>


            {/* Loading State */}
            {loading && (
              <div className="h-full w-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="h-full w-full flex items-center justify-center text-red-600 p-8 text-center">
                <p>{error}</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredImages.length === 0 && (
              <div className="h-full w-full flex items-center justify-center p-8 text-center">
                <p>No images found in this category. Please try another category or check back later.</p>
              </div>
            )}

            {/* Content when images are loaded */}
            {!loading && !error && filteredImages.length > 0 && (
              <>
                {/* Category Tabs */}
                <div className="p-4 bg-white border-b flex flex-wrap gap-2 justify-center">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setCurrentImageIndex(0); // Reset to first image when changing category
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-stone-800 text-white'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-hidden relative">
                  {/* Main Image with Error Handling */}
                  <div className="h-full w-full relative">
                    <img
                      src={filteredImages[currentImageIndex].url}
                      alt={filteredImages[currentImageIndex].title}
                      className="w-full h-full object-contain"
                      onError={handleImageError}
                    />
                    
                    {/* Caption Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 text-white">
                      <h3 className="text-2xl font-semibold mb-2">
                        {filteredImages[currentImageIndex].title}
                      </h3>
                      <p className="text-white/80">
                        {filteredImages[currentImageIndex].description}
                      </p>
                    </div>
                  </div>

                  {/* Navigation Arrows - only show if more than one image */}
                  {filteredImages.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 
                          bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full
                          transition-all duration-300 shadow-lg"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={handleNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 
                          bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full
                          transition-all duration-300 shadow-lg"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* Page Indicators - only show if more than one image */}
                  {filteredImages.length > 1 && (
                    <div className="absolute bottom-28 left-1/2 transform -translate-x-1/2 
                      flex gap-2 z-10">
                      {filteredImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 
                            ${currentImageIndex === index ? 'bg-white w-4' : 'bg-white/50'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}